import {Trans} from '@lingui/macro';
import React, {ReactElement} from 'react';
import styled from 'styled-components';

import {Renderer} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {computeCustomFieldIconName} from '../../../../helpers/customFieldViewHelpers';
import {CustomFieldModel} from '../../../../models/api/customFieldModel';
import {BoxedIcon} from '../../boxedIcon';
import {Skeleton, SkeletonColorSchemesEnum} from '../../skeleton';
import {
  getCellBackgroundColor,
  getCellIndexStyles,
  getCellTextColor,
  getTableCellAvatarSize,
} from '../helpers/tableCellHelpers';
import {getWidthSettingsStyles} from '../tableColumn';
import {TableCell, TableCellPropsWithAvatar, TableCellStyleProps} from './tableCell';

/*
 * Constants.
 */

const maxCheckboxTooltipWidth = 190; // px.

/*
 * Props.
 */

interface CustomFieldCellProps extends TableCellPropsWithAvatar<CustomFieldModel> {
  isDefaultField?: boolean;
  iconName?: IconName;
  renderDefaultPillTooltip?: Renderer;
}

/*
 * Styles.
 */

const StyledCustomFieldCellContainerDiv = styled.div<TableCellStyleProps>`
  padding: 0 12px;
  overflow: hidden;
  white-space: nowrap;
  display: flex;
  flex-flow: row;
  height: 100%;
  align-items: center;

  ${(p) => getCellBackgroundColor(p.$isHovering, p.$isSelected)}
  ${(p) => getWidthSettingsStyles(p.$settings?.cellWidth, p.$index)}
  ${(p) =>
    getCellIndexStyles(
      p.$index,
      p.$isSelected,
      p.$shouldRemoveTopBorderRadius,
      p.$shouldRemoveBottomBorderRadius,
    )}
`;

interface StyledCustomFieldAvatarDivProps {
  $avatarSize: number;
}
const StyledCustomFieldIconContainerDiv = styled.div<StyledCustomFieldAvatarDivProps>`
  flex: 0 0 ${(p) => p.$avatarSize}px;
  margin-right: 12px;
`;

const StyledCustomFieldDetailsContainerDiv = styled.div`
  flex: 1;
  display: flex;
  flex-flow: column;
  overflow: hidden;
`;

interface StyledCustomFieldNameDivProps {
  $isSelected: boolean;
}
const StyledCustomFieldNameDiv = styled.div<StyledCustomFieldNameDivProps>`
  display: flex;
  align-items: center;
  flex: 1;
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.bold};

  ${ellipsis()};
  ${(p) => getCellTextColor(p.$isSelected)}
`;

interface StyledCustomFieldDescriptionDivProps {
  $isSelected?: boolean;
}
const StyledCustomFieldDescriptionDiv = styled.div<StyledCustomFieldDescriptionDivProps>`
  flex: 1;
  font-size: ${fontSizes.verySmall};
  ${ellipsis()};
  padding-top: 4px;
  ${(p) => getCellTextColor(p.$isSelected, p.theme.greys.shade70)}

  &:empty {
    flex: 0;
    padding-top: 0;
  }
`;

const StyledCustomFieldDefaultPillDiv = styled.div`
  display: inline-block;
  padding: 2px 4px;
  border-radius: 7px;
  background: ${(p) => p.theme.greys.shade30};
  font-weight: ${fontWeights.bold};
  font-size: 8px;
  line-height: 10px;
  margin-left: 4px;
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  display: inline-block;
  pointer-events: all;
  margin-top: -2px;
`;

/*
 * Component.
 */

export const CustomFieldCell = (props: CustomFieldCellProps): ReactElement => {
  const theme = useUserTheme();
  const {
    isDefaultField,
    isSelected,
    isHovering,
    isLoading,
    size,
    settings,
    index,
    avatarSize,
    iconName,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
    renderDefaultPillTooltip,
  } = props;

  const avatarSizePx = getTableCellAvatarSize(size, avatarSize);

  return (
    <TableCell<CustomFieldModel>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={props.value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      renderLoading={() => (
        <StyledCustomFieldCellContainerDiv
          $settings={settings}
          $isHovering={false}
          $isSelected={Boolean(isSelected)}
          $index={index}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        >
          <StyledCustomFieldIconContainerDiv $avatarSize={avatarSizePx}>
            <Skeleton
              height={`${avatarSizePx}px`}
              width={`${avatarSizePx}px`}
              borderRadius="50%"
              scheme={settings?.loading?.style}
            />
          </StyledCustomFieldIconContainerDiv>
          <StyledCustomFieldDetailsContainerDiv>
            <StyledCustomFieldNameDiv $isSelected={false}>
              <Skeleton height="16px" width="140px" borderRadius="4px" scheme={settings?.loading?.style} />
            </StyledCustomFieldNameDiv>
            <StyledCustomFieldDescriptionDiv>
              <Skeleton
                height="16px"
                width="214px"
                borderRadius="4px"
                scheme={SkeletonColorSchemesEnum.GREY10}
              />
            </StyledCustomFieldDescriptionDiv>
          </StyledCustomFieldDetailsContainerDiv>
        </StyledCustomFieldCellContainerDiv>
      )}
      renderCell={(value) => (
        <StyledCustomFieldCellContainerDiv
          $settings={settings}
          $isHovering={Boolean(isHovering)}
          $index={index}
          $isSelected={Boolean(isSelected)}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        >
          <StyledCustomFieldIconContainerDiv $avatarSize={avatarSizePx}>
            <BoxedIcon
              name={iconName ?? computeCustomFieldIconName(value.type)}
              color={theme.greys.shade70}
              borderColor={theme.greys.shade30}
              borderStyle="solid"
              size={size}
            />
          </StyledCustomFieldIconContainerDiv>
          <StyledCustomFieldDetailsContainerDiv>
            <StyledCustomFieldNameDiv $isSelected={Boolean(isSelected)}>
              {value.name}
              {isDefaultField && (
                <StyledTooltipCoordinator
                  render={renderDefaultPillTooltip}
                  placement="top"
                  maxWidth={maxCheckboxTooltipWidth}
                >
                  <StyledCustomFieldDefaultPillDiv>
                    <Trans id="custom-field-tab-default-pill">DEFAULT</Trans>
                  </StyledCustomFieldDefaultPillDiv>
                </StyledTooltipCoordinator>
              )}
            </StyledCustomFieldNameDiv>
            <StyledCustomFieldDescriptionDiv $isSelected={Boolean(isSelected)}>
              {value.description}
            </StyledCustomFieldDescriptionDiv>
          </StyledCustomFieldDetailsContainerDiv>
        </StyledCustomFieldCellContainerDiv>
      )}
    />
  );
};
